/**
 * Enhanced Revenue Calculator for SAFECARE Act V2
 * Implements income-based revenue filters and configurable integration
 */

import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';
import { calculateEnrollmentRate } from './spendingCalculatorV2.js';

/**
 * Calculate effective income bases with revenue filters
 */
export function calculateEffectiveIncomeBases(year, baselineData, config, gdpMultiplier = 1.0) {
    const incomeRow = baselineData.incomeBase.find(d => d.year === year);
    if (!incomeRow) return null;

    // Apply coverage filters to each income source and scale by GDP Multiplier (Dynamic Economy)
    // Apply coverage filters to each income source and scale by GDP Multiplier (Dynamic Economy)
    // Default to 1.0 (full coverage) if config is missing to prevent NaN
    const effectiveWages = incomeRow.wages * (config.wageCoverage ?? 1.0) * gdpMultiplier;
    const effectiveSelfEmp = incomeRow.selfEmployment * (config.selfEmploymentCoverage ?? 1.0) * gdpMultiplier;
    const effectivePassThrough = incomeRow.passThrough * (config.passThroughCoverage ?? 1.0) * gdpMultiplier;
    const effectiveCapital = incomeRow.capital * (config.capitalCoverage ?? 1.0) * gdpMultiplier;

    // Calculate retirement income after exemption
    // "Invented" per-filer exemption removed. 
    // Ideally we would subtract (Pop * Limit) but distributions matter. 
    // For now, we assume the incomeBase reflects potentially taxable retirement income or we apply no broad deduction.
    const retirementExemptionTotal = 0;
    const effectiveRetirement = Math.max(0, incomeRow.retirement - retirementExemptionTotal);

    // Total non-wage income before cap
    const nonWageTotal = effectiveSelfEmp + effectivePassThrough + effectiveCapital + effectiveRetirement;

    // Apply non-wage cap if set
    let effectiveNonWage = nonWageTotal;
    if (config.nonWageCap && config.nonWageCap > 0) {
        // Rough approximation: assume 20% of non-wage income is above cap
        const fractionAboveCap = config.nonWageCap < 250000 ? 0.25 :
            config.nonWageCap < 500000 ? 0.15 : 0.10;
        effectiveNonWage = nonWageTotal * (1 - fractionAboveCap);
    }

    return {
        wageBase: effectiveWages,
        nonWageBase: effectiveNonWage,
        totalBase: effectiveWages + effectiveNonWage
    };
}

/**
 * Calculate individual contribution for a given income level
 * Applies base rate + progressive surtaxes
 */
export function calculateIndividualContribution(income, config) {
    let contribution = income * config.baseIndividualRate;

    // Apply surtaxes progressively
    // Apply surtaxes progressively
    const surtaxThresholds = config.surtaxThresholds || [];
    const surtaxRates = config.surtaxRates || [];

    for (let i = 0; i < surtaxThresholds.length; i++) {
        const threshold = surtaxThresholds[i];
        const rate = surtaxRates[i] || 0;

        if (income > threshold) {
            const taxableAboveThreshold = income - threshold;
            const nextThreshold = surtaxThresholds[i + 1] || Infinity;
            const cappedAmount = Math.min(taxableAboveThreshold, nextThreshold - threshold);
            contribution += cappedAmount * rate;
        }
    }

    return contribution;
}

/**
 * Calculate aggregate SAFECARE revenues for a given year
 */
/**
 * Calculate aggregate SAFECARE revenues for a given year
 */
export function calculateAggregateRevenues(year, baselineData, config, enrollmentRate, gdpMultiplier = 1.0) {
    const yearData = baselineData.nheBaseline.find(d => d.year === year);
    if (!yearData) return null;

    const gdp = yearData.gdp * gdpMultiplier;

    // Determine enrollment rate for contribution alignment
    // Use passed rate if available, otherwise calculate it
    const effectiveEnrollmentRate = (typeof enrollmentRate === 'number')
        ? enrollmentRate
        : calculateEnrollmentRate(year, config);

    // Get effective income bases with filters
    const incomeBases = calculateEffectiveIncomeBases(year, baselineData, config, gdpMultiplier);
    if (!incomeBases) return null;

    // Employer contributions (Section 202) - on wage base
    // Scaled by enrollment (Contribution Alignment)
    const payrollBase = gdp * 0.37; // 37% of GDP per v2.0 spec (labor share declining)
    const employerRevenue = payrollBase * config.employerRate * effectiveEnrollmentRate;

    // Individual contributions (Section 203) - on total filtered income base
    // Scaled by enrollment
    const baseIndividualRevenue = incomeBases.totalBase * config.baseIndividualRate * effectiveEnrollmentRate;

    // Estimate surtax revenue (affected by revenue filters)
    // If capital/non-wage is excluded, surtaxes are lower
    // UPDATED: Sensitivity increased from 0.3 to 0.6 to reflect that high earners (surtax payers)
    // derive a majority of their income from capital.
    const surtaxFactor = 0.15 * (1 - (1 - config.capitalCoverage) * 0.6);
    const surtaxRevenue = baseIndividualRevenue * surtaxFactor;

    const individualRevenue = baseIndividualRevenue + surtaxRevenue;

    // Self-employment contributions
    // Scaled by enrollment
    const selfEmployedBase = payrollBase * 0.10;
    const selfEmployedRevenue = selfEmployedBase * config.baseSelfEmployedRate * effectiveEnrollmentRate;

    // CRITICAL: Calculate redirected federal health spending (Title X)
    // This represents what SAFECARE actually costs for federal beneficiaries
    // NOT the full baseline federal budget
    const baselineFiscal = baselineData.federalDebtBaseline.find(d => d.year === year);
    const federalHealthOutlays = baselineFiscal ? baselineFiscal.healthOutlays : 0;

    // Calculate integration rate with ramp
    const yearsFromStart = year - config.startYear;
    let integrationRate = config.initialIntegrationShare;

    if (yearsFromStart > 0 && yearsFromStart < config.integrationRampYears) {
        // Linear ramp to 100%
        const rampProgress = yearsFromStart / config.integrationRampYears;
        integrationRate = config.initialIntegrationShare +
            (1.0 - config.initialIntegrationShare) * rampProgress;
    } else if (yearsFromStart >= config.integrationRampYears) {
        integrationRate = 1.0;
    }

    // Federal beneficiary share based on phase-in schedule (Sec 1005)
    // Medicare + Medicaid + CHIP + ACA + VA ≈ 60% of total enrollment
    const federalBeneficiaryShare = 0.60;

    // IMPORTANT: redirectedFederalSpending is NOT the baseline budget
    // It's what SAFECARE actually costs for federal beneficiaries
    // We'll calculate this as a placeholder here, but it should be updated
    // in runScenario.js once we know the actual SAFECARE spending
    const redirectedFederalSpending = federalHealthOutlays * integrationRate;

    // Copayment revenues (Section 204)
    // Scaled by enrollment
    const totalPopulation = 335000000;
    const avgCopaymentPerPerson = config.avgCopay * 20; // Assume ~20 visits/year average
    const cappedCopayment = Math.min(avgCopaymentPerPerson, config.annualCopaymentCap);
    const copaymentRevenue = totalPopulation * 0.5 * cappedCopayment * effectiveEnrollmentRate;

    // Total SAFECARE revenues
    const totalRevenue = employerRevenue + individualRevenue + selfEmployedRevenue +
        redirectedFederalSpending + copaymentRevenue;

    return {
        year,
        employerRevenue,
        individualRevenue,
        selfEmployedRevenue,
        redirectedFederalSpending,
        copaymentRevenue,
        integrationRate,
        totalRevenue,
        revenueAsPercentGDP: totalRevenue / gdp
    };
}

/**
 * Calculate revenues for a single year with specific rate configuration
 * This enables year-by-year rate adjustments based on Trust Fund reserve ratios
 * 
 * @param {number} year - The year to calculate
 * @param {object} baselineData - Baseline economic data
 * @param {object} config - Full configuration object (for filters, integration, etc.)
 * @param {object} rateConfig - Year-specific rate configuration
 * @param {number} rateConfig.employerRate - Employer contribution rate
 * @param {number} rateConfig.baseIndividualRate - Individual base rate
 * @param {number} rateConfig.baseSelfEmployedRate - Self-employed base rate
 * @param {array} rateConfig.surtaxThresholds - Surtax income thresholds
 * @param {array} rateConfig.surtaxRates - Surtax rates
 * @param {number} enrollmentRate - Optional enrollment rate for contribution alignment
 * @returns {object} Revenue breakdown for the year
 */
// ----------------------------------------------------------------------------
// PRIMARY CALCULATION FUNCTION: YEAR-SPECIFIC REVENUE
// ----------------------------------------------------------------------------
export function calculateYearRevenue(year, baselineData, config, rateConfig, enrollmentRate, gdpMultiplier = 1.0, totalSpending, eventMultipliers = {}) {
    const yearData = baselineData.nheBaseline.find(d => d.year === year);
    if (!yearData) return null;

    const gdp = yearData.gdp * gdpMultiplier;

    // determine enrollment rate for contribution alignment
    const effectiveEnrollmentRate = (typeof enrollmentRate === 'number')
        ? enrollmentRate
        : calculateEnrollmentRate(year, config);

    // DEBUGGING NaN REVENUE
    if (year === 2027) {
        // console.log(`[RevenueCalc] Year 2027 Diagnostic:`);
        // console.log(`- GDP: ${gdp}`);
        // console.log(`- WageCoverage: ${config.wageCoverage}`);
        // console.log(`- StartYear: ${config.startYear}`);
        // console.log(`- EmployerRate: ${rateConfig.employerRate}`);
        // console.log(`- EnrollmentRate: ${effectiveEnrollmentRate}`);
        // console.log(`- PayrollBaseErosion: ${config.payrollBaseErosion}`);
    }

    // FIX #1: Deriving bases correctly from GDP since we don't have separate incomeBase projections for all years
    // Using standard macroeconomic shares:
    // Labor Share ~45% (Wages)
    // Capital/Profits ~40% (Capital + Pass-through)
    // Mixed/Self-Emp ~10%
    // These are consistent with CBO long-term projections

    // === ROBUST REVENUE CALCULATION ===
    // Config Defaults
    const wageCover = config.wageCoverage ?? 1.0;
    const selfEmpCover = config.selfEmploymentCoverage ?? 1.0;
    const capitalCover = config.capitalCoverage ?? 0.0;
    const taxEvasion = config.taxEvasionRate ?? 0.12;
    const erosion = config.payrollBaseErosion ?? 0.02;
    const wageGrowth = config.wageGrowthDifferential ?? 0.005;
    const surtaxAvoid = config.surtaxAvoidance ?? 0.35;

    // Time-based factors
    const yearsFromStart = Math.max(0, year - (config.startYear || 2027));
    const wageGrowthFactor = Math.pow(1 + wageGrowth, yearsFromStart);
    const erosionFactor = Math.pow(1 - erosion, yearsFromStart);
    const complianceFactor = 1.0 - taxEvasion;

    // 0. Estimate Population (Moved up for Per-Capita Thresholds)
    // Use yearData.population if available, else derive from estimate
    // Proxy: 335M + 0.5% growth/year
    const estimatedPop = 335000000 * Math.pow(1.005, yearsFromStart);
    const totalPopulation = yearData.population || estimatedPop;

    // 1. Calculate Bases from GDP (Standard Economic Ratios)
    // Wages & Salaries: ~45% of GDP
    const rawWageBase = gdp * 0.45 * wageGrowthFactor;
    // Self-Employment Income: ~8% of GDP
    const rawSelfEmpBase = gdp * 0.08 * wageGrowthFactor; // Assuming correlation
    // Total Personal Income (for Individual Tax): ~60% of GDP
    // Includes wages, dividends, interest, transfers, etc.
    // We adjust this based on capital coverage settings
    const rawIndividualBase = gdp * 0.60;

    // 2. Adjust Bases
    // Employer Payroll Base: Wages * Erosion * Coverage
    const payrollBase = rawWageBase * erosionFactor * wageCover;

    // Individual Income Base: 
    // Proxy: 70% of Personal Income is Labor-like, 30% is Capital-like.
    const laborSharePI = 0.70;
    const capitalSharePI = 0.30; // Capital + Pass-through + Rent

    // --- APPLY TAX THRESHOLDS (User Controls) ---

    // A. Standard Deduction / Income Exemption (The "200% PL" threshold)
    // We approximate the aggregate reduction in the tax base.
    // Est. Tax Units (Returns) ≈ Population / 2.1
    const taxUnits = (totalPopulation || 335000000) / 2.1;
    const exemptionThreshold = config.incomeExemptionThreshold || 0;
    // Limits: We can't deduct more than the aggregate income of the bottom bracket.
    // A simplified progressive approach: Subtract (Units * Threshold) from base.
    // Effectiveness factor: 0.8 (some returns have less income than the deduction)
    const aggregateExemption = taxUnits * exemptionThreshold * 0.8;

    const rawLaborBase = rawIndividualBase * laborSharePI;
    const taxableLaborBase = Math.max(0, rawLaborBase - aggregateExemption);

    // B. Investment Income Threshold
    // We use a distribution curve to estimate % of capital income held by filers ABOVE the threshold.
    // Decay curve: 85% of capital income starts above $250k. 50% starts above $1.5M.
    // Formula: exp(-threshold / 1,500,000)
    const investThreshold = config.investmentTaxThreshold || 0;
    const capitalConcentrationScale = 1500000;
    const percentCapitalTaxable = Math.exp(-investThreshold / capitalConcentrationScale);

    const effectiveCapitalShare = capitalSharePI * capitalCover * percentCapitalTaxable;

    // Reconstruct Total Base
    // Note: We apply compliance factor to the final result
    const adjustedIndividualBase = (taxableLaborBase + (rawIndividualBase * effectiveCapitalShare)) * complianceFactor;

    // Self-Employment Base
    const adjustedSelfEmpBase = rawSelfEmpBase * selfEmpCover * complianceFactor;

    // 3. Calculate Revenues
    // Employer Tax (Sec 202): 8% of Payroll Base
    // Effective Rate = rate * compliance
    const employerRevenue = payrollBase * complianceFactor * (rateConfig.employerRate || 0.08) * effectiveEnrollmentRate;

    // Individual Tax (Sec 203): 3.5% of Individual Base
    const individualRevenue = adjustedIndividualBase * (rateConfig.baseIndividualRate || 0.035) * effectiveEnrollmentRate;

    // Self-Employed Tax: 11.5% of SE Base
    const selfEmployedRevenue = adjustedSelfEmpBase * (rateConfig.baseSelfEmployedRate || 0.115) * effectiveEnrollmentRate;

    // Surtaxes (Progressive)
    // Base: High Income Share (approx 15% of GDP) but depends on inequality
    // We'll estimate surtax revenue as a % of Individual Revenue for simplicity 
    // or use the explicit base if required.
    // User requested explicit surtax calculation.
    // Surtax Base = 15% of GDP
    const surtaxBase = gdp * 0.15 * complianceFactor * (1 - surtaxAvoid);

    // Interactive Surtax Calculation
    // Bracket 1 ($100k-$250k): ~50% of high income base
    // Bracket 2 ($250k-$500k): ~30%
    // Bracket 3 ($500k+): ~20%
    const rates = rateConfig.surtaxRates || [0.01, 0.015, 0.02];
    let surtaxRevenue = 0;
    surtaxRevenue += (surtaxBase * 0.50) * rates[0];
    surtaxRevenue += (surtaxBase * 0.30) * rates[1];
    surtaxRevenue += (surtaxBase * 0.20) * rates[2];

    // Surtax is also scaled by enrollment? Technically it's a tax on ALL income, 
    // but usually tied to program participation in these models. 
    // Let's assume broad tax, but for consistency with others we scale it 
    // OR we treat it as general revenue. 
    // The previous model scaled everything by enrollment.
    surtaxRevenue *= effectiveEnrollmentRate;

    // Add Surtax to Individual Revenue line item for total
    // (Or keep separate if needed, but return object has specific keys)
    const totalIndividualRevenue = individualRevenue + surtaxRevenue;
    // 4. Federal Redirect Calculation (FIXED)
    const safeTotalSpending = totalSpending || 0;
    // DEBUG: Federal Redirect

    // Calculate integration rate (Title X integration timing proxy).
    //
    // IMPORTANT:
    // - Before the effective implementation date (startYear + legalDelay), integration is 0.
    // - In the first effective year, integration should be the configured initialIntegrationShare
    //   (do NOT arbitrarily halve it).
    const legalDelay = config.legalChallengeDelay ?? 0;
    const startYear = config.startYear ?? 2027;
    const effectiveStartYear = startYear + legalDelay;
    const effectiveYearIndex = year - effectiveStartYear; // 0-based once active

    const initialShare = config.initialIntegrationShare ?? 0.90;
    const rampYears = config.integrationRampYears ?? 7;

    let integrationRate = 0;
    if (effectiveYearIndex < 0) {
        integrationRate = 0;
    } else if (effectiveYearIndex === 0) {
        integrationRate = initialShare;
    } else if (effectiveYearIndex > 0 && effectiveYearIndex < rampYears) {
        const rampProgress = effectiveYearIndex / rampYears;
        integrationRate = initialShare + (1.0 - initialShare) * rampProgress;
    } else {
        integrationRate = 1.0;
    }

    const redirectedFederalSpending = safeTotalSpending * 0.60 * integrationRate;

    // 5. Copayment revenues (Section 204)
    // Population is now defined at top of function

    const avgCopaymentPerPerson = (config.avgCopay || 10) * 20; // 20 visits/year
    const annualCap = config.annualCopaymentCap || 750;
    const cappedCopayment = Math.min(avgCopaymentPerPerson, annualCap);
    // 80% utilization factor as per user request
    const copaymentRevenue = totalPopulation * 0.8 * cappedCopayment * effectiveEnrollmentRate;

    // 6. State Maintenance of Effort (MOE)
    // State share is ~17% (20% of Program Cost * Integration? or Baseline?)
    // User requested: spending.totalProgramCost * 0.20 * config.stateCooperation * integrationRate
    const stateCoop = config.stateCooperation ?? 1.0;
    const stateRevenue = safeTotalSpending * 0.20 * stateCoop * integrationRate;

    // Total SAFECARE revenues
    const leakageFactor = 1.0 - (config.medicalTourismLeakage || 0.03);

    // Sum it all up
    // Note: totalIndividualRevenue (defined above) includes surtax.
    const totalRevenue = (employerRevenue + totalIndividualRevenue + selfEmployedRevenue +
        redirectedFederalSpending + stateRevenue + copaymentRevenue) * leakageFactor * (eventMultipliers.revenue || 1.0);

    return {
        year,
        employerRevenue,
        individualRevenue: totalIndividualRevenue, // Now includes surtax
        surtaxRevenue, // Added for diagnostics
        selfEmployedRevenue,
        revenueFederalRedirect: redirectedFederalSpending, // updated key name to match
        revenueStateRedirect: stateRevenue,
        copaymentRevenue,
        integrationRate,
        totalRevenue,
        revenueAsPercentGDP: totalRevenue / gdp,
        // Include the rates that were used for transparency
        ratesApplied: {
            employerRate: rateConfig.employerRate,
            baseIndividualRate: rateConfig.baseIndividualRate,
            baseSelfEmployedRate: rateConfig.baseSelfEmployedRate
        }
    };
}

/**
 * Calculate revenues for all years in the baseline
 */
export function calculateRevenueProjection(baselineData, config) {
    // Filter baseline to start from config.startYear
    const startIndex = baselineData.nheBaseline.findIndex(d => d.year === config.startYear);
    const simulationYears = config.simulationYears || 21;
    const relevantYears = baselineData.nheBaseline.slice(startIndex, startIndex + simulationYears);

    return relevantYears.map(yearData => {
        const enrollmentRate = calculateEnrollmentRate(yearData.year, config);
        return calculateAggregateRevenues(yearData.year, baselineData, config, enrollmentRate);
    }).filter(r => r !== null);
}
